import { useCallback } from 'react';

import * as Sentry from '@sentry/browser';
import { isAxiosError } from '~api/isAxiosError';
import {
  GlobalRateLimitExceededError,
  QuotaExceededError,
  RateLimitExceededError,
  ServiceUnavailableError,
  parseAi429Error,
  parseServiceUnavailableError,
} from '~api/QuotaExceededSchema';

import { AiUsageLimitEventAdapter } from '~analytics/adapters/AiUsageLimitEventAdapter';
import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';

export type FeatureTag =
  | 'ai-extract-questions'
  | 'ai-generate-questions'
  | 'ai-import-from-text'
  | 'ai-areas-of-improvement'
  | 'ai-specific-students'
  | 'ai-generate-insights';

interface UseAiErrorHandlerOptions {
  onQuotaExceeded?: (error: QuotaExceededError) => Promise<void>;
  onRateLimitExceeded?: (error: RateLimitExceededError) => Promise<void>;
  onGlobalRateLimitExceeded?: (
    error: GlobalRateLimitExceededError
  ) => Promise<void>;
  onServiceUnavailable?: (error: ServiceUnavailableError) => Promise<void>;
  onOtherError?: (error: unknown) => void;
  featureTag: FeatureTag;
}

export function useAiErrorHandler({
  onQuotaExceeded = defaultOnQuotaExceeded,
  onRateLimitExceeded = defaultOnRateLimitExceeded,
  onGlobalRateLimitExceeded = defaultOnGlobalRateLimitExceeded,
  onServiceUnavailable = defaultOnServiceUnavailable,
  onOtherError,
  featureTag,
}: UseAiErrorHandlerOptions) {
  const defaultOnOtherError = useCallback(
    (error: unknown) => {
      Sentry.captureException(error, {
        tags: { feature: featureTag },
      });
      toast.critical(
        translate('An unexpected error occurred. Please try again later.')
      );
    },
    [featureTag]
  );

  const handleOtherError = onOtherError ?? defaultOnOtherError;

  return useCallback(
    async (error: unknown) => {
      try {
        if (isAxiosError(error) && error.response?.status === 503) {
          const parsed503 = parseServiceUnavailableError(error);
          if (parsed503) {
            await onServiceUnavailable(parsed503);
            return;
          }
          handleOtherError(error);
          return;
        }

        if (!isAxiosError(error) || error.response?.status !== 429) {
          handleOtherError(error);
          return;
        }

        const parsed = parseAi429Error(error);
        if (!parsed) {
          Sentry.captureException(error, {
            level: 'warning',
            tags: { feature: featureTag, unrecognized_429: 'true' },
          });
          toast(
            translate('Rate Limit Exceeded. Wait a few moments and try again.')
          );
          return;
        }

        switch (parsed.kind) {
          case 'quota_exceeded':
            await onQuotaExceeded(parsed);
            return;
          case 'rate_limit_exceeded':
            await onRateLimitExceeded(parsed);
            return;
          case 'global_rate_limit_exceeded':
            await onGlobalRateLimitExceeded(parsed);
            return;
          default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _exhaustive: never = parsed;
            handleOtherError(error);
          }
        }
      } catch (handlerError) {
        Sentry.captureException(handlerError, {
          tags: { feature: featureTag },
        });
        toast.critical(
          translate('An unexpected error occurred. Please try again later.')
        );
      }
    },
    [
      featureTag,
      handleOtherError,
      onQuotaExceeded,
      onRateLimitExceeded,
      onGlobalRateLimitExceeded,
      onServiceUnavailable,
    ]
  );
}

async function defaultOnQuotaExceeded() {
  new AiUsageLimitEventAdapter({
    limitType: 'quota_exceeded',
  }).sendAnalytics();
  toast(
    translate(
      "You've reached your usage limit. Please try again later or upgrade your plan."
    )
  );
}

async function defaultOnRateLimitExceeded() {
  new AiUsageLimitEventAdapter({
    limitType: 'rate_limit_exceeded',
  }).sendAnalytics();
  toast(translate('Rate Limit Exceeded. Wait a few moments and try again.'));
}

async function defaultOnGlobalRateLimitExceeded() {
  new AiUsageLimitEventAdapter({
    limitType: 'global_rate_limit_exceeded',
  }).sendAnalytics();
  toast(translate('Feature is at capacity. Please try again later.'));
}

async function defaultOnServiceUnavailable() {
  new AiUsageLimitEventAdapter({
    limitType: 'service_unavailable',
  }).sendAnalytics();
  toast(translate('Feature is at capacity. Please try again later.'));
}
